/*
 * Boot. Order of operations:
 *  1. Load CSS so the layout is right before Pixi attaches.
 *  2. Load persisted save (or fall back to defaults).
 *  3. Show the welcome modal on first run (or skip if returning player).
 *  4. Init the customizer modal's controls (uses `save`, so must follow load).
 *  5. Start the game loop, which mounts the Pixi app.
 */

import '@/ui/ui.css';
import { bumpSessionStreak, loadSave, persist, save } from '@/state/save';
import { wireCloseButtons, openModal, $ } from '@/ui/modal';
import { initCustomizer, rebuildHats, rebuildSwatches } from '@/ui/customizer';
import { startGame, emitSayPhrase } from '@/game';
import { installAudioToggle } from '@/ui/audio-toggle';
import { checkAndShowDailyDrop } from '@/state/dailyDrop';

async function boot(): Promise<void> {
  wireCloseButtons();

  let loaded = false;
  try {
    loaded = await loadSave();
  } catch {
    // ignore — leave defaults
  }

  // Bump the consecutive-sessions streak now that we've loaded existing save
  // data (or fallen back to defaults). This feeds into chatCount/streak-style
  // hat unlocks; harmless on first run (streak = 1).
  bumpSessionStreak();

  // Initialize customizer so swatch / hat lists are built. This reads `save`,
  // so we wait for loadSave to settle first.
  initCustomizer({
    onPhrase: (p) => emitSayPhrase(p),
  });

  if (!loaded || save.firstRun) {
    ($('welcome-name') as HTMLInputElement).value = save.name;
    openModal('welcome-modal');
  } else {
    rebuildHats();
    rebuildSwatches();
  }

  // Welcome modal "Step into the square" persists name + clears first-run flag.
  $('welcome-go').addEventListener('click', () => {
    const v = ($('welcome-name') as HTMLInputElement).value.trim();
    if (v) save.name = v;
    save.firstRun = false;
    void persist();
  });

  // Wire the audio mute toggle. The engine stays locked until the first click
  // (browser autoplay policy); ambient bed starts on unlock if not muted.
  const audioBtn = document.getElementById('audio-toggle');
  if (audioBtn) installAudioToggle(audioBtn);

  const stage = document.getElementById('pixi-stage');
  if (!stage) throw new Error('Missing #pixi-stage');
  await startGame(stage);

  // Daily drop — show once per calendar day. Safe to call after startGame; the
  // toast is a separate modal so it sits cleanly above the welcome flow.
  checkAndShowDailyDrop();
}

boot().catch((e) => {
  console.error('boot failed:', e);
  // Even on failure, show the welcome modal so the user sees *something*.
  try {
    const w = document.getElementById('welcome-modal');
    w?.classList.add('active');
  } catch {
    /* nothing more to do */
  }
});
